/**
 * Счётчики посещаемости: контейнер Google Tag Manager и Яндекс.Метрика.
 *
 * Идентификаторы приходят из переменных сборки (`VITE_GTM_ID`, `VITE_YM_ID`).
 * Пусто — счётчик не подключается вовсе: в разработке и в тестах чужие скрипты
 * не грузятся, а статистику не портят заходы с localhost.
 *
 * Через GTM сайт не знает, какие теги внутри: GA4, реклама или пиксель — это
 * решается в интерфейсе GTM без правки кода и без пересборки. Код обязан лишь
 * сообщать контейнеру, что происходит на странице.
 *
 * Сайт — SPA: переход между страницами не перезагружает документ, и счётчики
 * сами увидят только первый экран. Поэтому переходы отправляются вручную —
 * см. `usePageViews`.
 */
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

// Опциональная цепочка нужна вне сборки Vite (тесты в Node), где import.meta.env нет
const env = import.meta.env as ImportMetaEnv | undefined;
const GTM_ID = (env?.VITE_GTM_ID as string | undefined) ?? '';
const YM_ID = (env?.VITE_YM_ID as string | undefined) ?? '';

declare global {
  interface Window {
    dataLayer?: Record<string, unknown>[];
    ym?: (...args: unknown[]) => void;
  }
}

function loadScript(src: string): void {
  const script = document.createElement('script');
  script.src = src;
  script.async = true;
  document.head.appendChild(script);
}

/** Подключает счётчики один раз за загрузку страницы. */
export function setupAnalytics(): void {
  if (GTM_ID) {
    window.dataLayer = window.dataLayer ?? [];
    window.dataLayer.push({ 'gtm.start': Date.now(), event: 'gtm.js' });
    loadScript(`https://www.googletagmanager.com/gtm.js?id=${GTM_ID}`);
  }

  if (YM_ID) {
    /*
     * Очередь вызовов до загрузки самого скрипта: иначе первое обращение
     * к `ym` упало бы, а вместе с ним и отрисовка страницы.
     */
    window.ym =
      window.ym ??
      function ym(...args: unknown[]) {
        const queued = window.ym as unknown as { a?: unknown[][] };
        queued.a = queued.a ?? [];
        queued.a.push(args);
      };
    window.ym(Number(YM_ID), 'init', {
      webvisor: true,
      clickmap: true,
      trackLinks: true,
      accurateTrackBounce: true,
      // Просмотры шлём сами — см. usePageViews
      defer: true,
    });
    loadScript(`https://mc.yandex.ru/metrika/tag.js?id=${YM_ID}`);
  }
}

/**
 * Сообщает о переходе на новый адрес: в GTM — событием `page_view`
 * в dataLayer (по нему в контейнере настраивается триггер), в Метрику —
 * обычным хитом.
 */
export function usePageViews(): void {
  const location = useLocation();
  const previous = useRef<string | null>(null);

  useEffect(() => {
    const path = location.pathname + location.search;
    // React в строгом режиме вызывает эффект дважды — просмотр был бы двойным
    if (previous.current === path) return;

    const from = previous.current;
    previous.current = path;

    const url = window.location.origin + path;
    window.dataLayer?.push({ event: 'page_view', page_path: path, page_location: url });
    window.ym?.(Number(YM_ID), 'hit', url, { referer: from ?? document.referrer });
  }, [location.pathname, location.search]);
}
